// ─────────────────────────────────────────────────────────────────────────────
// Sourcing region definitions
//
// Groups US states into three sourcing regions that map to how the feeder
// cattle supply chain actually operates:
//
//   - Plains: the feedlot corridor + their primary cow-calf sourcing states
//   - Cornbelt+SE: cow-calf / stocker source regions east of the Plains
//   - West: mountain west / desert west
//
// The same mapping is used by the Sell Decision RPC (see
// supabase/migrations/20260408000015_get_sell_decision_data_rpc.sql) so any
// tool that lets the user filter by sourcing region shares one set of bounds.
// ─────────────────────────────────────────────────────────────────────────────

export type SourcingRegion = "Plains" | "Cornbelt+SE" | "West";

export const SOURCING_REGIONS: SourcingRegion[] = ["Plains", "Cornbelt+SE", "West"];

export const REGION_STATES: Record<SourcingRegion, string[]> = {
  Plains: ["CO", "KS", "MT", "ND", "NE", "NM", "OK", "SD", "TX", "WY"],
  "Cornbelt+SE": [
    "AL", "AR", "FL", "GA", "IA", "IL", "IN", "KY", "LA",
    "MN", "MO", "MS", "NC", "OH", "SC", "TN", "VA", "WI", "WV",
  ],
  West: ["AK", "AZ", "CA", "HI", "ID", "NV", "OR", "UT", "WA"],
};

/**
 * Get the state list for a region, or null if the region is "All" (no
 * filter) or unrecognized.
 */
export function getStatesForRegion(region: SourcingRegion | "All"): string[] | null {
  if (region === "All") return null;
  return REGION_STATES[region] ?? null;
}

/**
 * Inverse lookup — given a 2-letter state alpha, return the sourcing
 * region it belongs to, or null when unmapped (CT, DE, MA, MD, ME, MI,
 * NH, NJ, NY, PA, RI, VT — minor cattle states intentionally excluded
 * from the regional rollups).
 */
export function regionForState(stateAlpha: string): SourcingRegion | null {
  const upper = stateAlpha.toUpperCase();
  for (const region of SOURCING_REGIONS) {
    if (REGION_STATES[region].includes(upper)) return region;
  }
  return null;
}

export const REGION_LABELS: Record<SourcingRegion | "All", string> = {
  All: "All Regions",
  Plains: "Plains (CO, KS, NE, OK, TX, …)",
  "Cornbelt+SE": "Cornbelt + Southeast",
  West: "West",
};

// ─────────────────────────────────────────────────────────────────────────────
// US state name → USPS 2-letter alpha mapping.
//
// `dv_sale_market_reports.state` and the `dv_daily_cohort_stats` rollup both
// store the 2-letter alpha ("KS", not "Kansas"). UI surfaces that parse a
// human-readable state name out of a slug or label MUST translate before
// querying. See FreeReportPage for the canonical caller.
// ─────────────────────────────────────────────────────────────────────────────

export const STATE_NAME_TO_ALPHA: Record<string, string> = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR",
  "California": "CA", "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE",
  "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
  "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS",
  "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN",
  "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", "Nebraska": "NE",
  "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC",
  "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR",
  "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT",
  "Vermont": "VT", "Virginia": "VA", "Washington": "WA",
  "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY",
};

/**
 * Translate a full state name ("Kansas") to its USPS 2-letter alpha ("KS").
 * Returns null when the input is not a recognized US state name. Match is
 * case-sensitive against title-case US state names.
 */
export function stateNameToAlpha(name: string): string | null {
  return STATE_NAME_TO_ALPHA[name.trim()] ?? null;
}
